import { isEmpty } from "lodash";

export default class OnLineForm {
    formContainer: HTMLDivElement;
    form: HTMLFormElement;
    formOriginalSubmit: HTMLInputElement;
    customButtonHtml: DocumentFragment;
    beforeText: HTMLDivElement;
    afterText: HTMLDivElement;
    successText: HTMLDivElement;
    fieldErrorMessage: string;
    isValid = true;
    constructor (private formContainerId: string, private customSubmitID: string, private errorMessage?: string) {
        if (this.initElements()) {
            this.saveCustomElements();
            this.initForm();
        }
        else
            console.error(`Form with container ID ${this.formContainerId} could not be initialised.`);
        this.initObserver();
    }

    public reInit() {
        if (this.initElements())
            this.initForm();
    }

    private initElements(): boolean {
        this.formContainer = document.getElementById(this.formContainerId) as HTMLDivElement;
        this.form = this.formContainer?.querySelector<HTMLFormElement>('form');
        this.formOriginalSubmit = this.formContainer.querySelector('input[type="submit"]');

        this.beforeText = this.formContainer?.querySelector('.form-before-text');
        this.afterText = this.formContainer?.querySelector('.form-after-text');
        this.successText = this.formContainer?.querySelector('.form-after-submit');

        return !!(this.formContainer && this.form && this.formOriginalSubmit);
    }

    private saveCustomElements() {
        // Check if the custom elements are present
        this.customButtonHtml = new DocumentFragment();
        const customElementWrapper = this.formContainer.querySelector<HTMLDivElement>('.form-custom-button-html');
        this.customButtonHtml.appendChild(customElementWrapper);
    }

    private initForm() {
        this.form.setAttribute('novalidate', '');
        if (this.errorMessage) 
            this.fieldErrorMessage = this.errorMessage;
        if (this.customButtonHtml.childNodes.length == 0) 
            return;

        this.addCustomSubmitToForm();
    }

    private initObserver() {
        const observer = new FormObserver(this);
        observer.startObserving();
    }

    public addCustomSubmitToForm() {
        // Search for the new submit button
        // Clone the document fragment so that the original document fragment remains unchanged when appending clone to the DOM
        const clonedHtml = this.customButtonHtml.cloneNode(true) as DocumentFragment;
        const customSubmit = clonedHtml.getElementById(this.customSubmitID);
        if (!customSubmit) return;

        customSubmit.addEventListener("click", (event: Event) => {
            this.isValid = true;

            this.ensureRadioButtonSelection();
            this.ensureCheckboxSelected();

            if (!this.isValid) {
                event.preventDefault(); // Prevent default form submission
            }
        });

        // Add to the DOM directly above the original submit.
        this.formOriginalSubmit.parentNode?.insertBefore(clonedHtml, this.formOriginalSubmit);
    }

    private ensureRadioButtonSelection() {
        const allRadioList = this.form.querySelectorAll(".ktc-radio-list[aria-required='true']");
        this.isValid = true;
        if (allRadioList == null || allRadioList.length == 0) return;
        allRadioList.forEach(radioList => {
            const allRadioButtons = Array.from(radioList.querySelectorAll<HTMLInputElement>('.ktc-radio input'));
            if (!allRadioButtons.some(x => x.checked)) {
                this.isValid = false;
                radioList.setAttribute('aria-invalid', 'true');
            } else {
                radioList.setAttribute('aria-invalid', 'false');
            }
        });

        if (!this.isValid) {
            this.showValidationMessage();
        }
    }

    private ensureCheckboxSelected() {
        const multiCheckboxes = this.form.querySelectorAll<HTMLInputElement>(".multi-checkbox[aria-required='true']");
        this.isValid = true;
        if (multiCheckboxes == null || multiCheckboxes.length == 0) return;
        multiCheckboxes.forEach(multiCheckBox => {
            if (isEmpty(multiCheckBox.value)) {
                this.isValid = false;
                multiCheckBox.setAttribute('aria-invalid', 'true');
            } else {
                multiCheckBox.setAttribute('aria-invalid', 'false');
            }
        });

        if (!this.isValid) {
            this.showValidationMessage();
        }
    }

    showValidationMessage() {
        const allFields = this.form.querySelectorAll<HTMLInputElement>('input.form-control, textarea.form-control');
        const allRadioButtonsWrapper = this.form.querySelectorAll<HTMLElement>('.ktc-radio-list');
        const allMultiCheckboxWrapper = this.form.querySelectorAll<HTMLElement>(".form-field .editing-form-control-nested-control:has(input.multi-checkbox[aria-required='true'])");
        let errorMessage: string;
        allFields.forEach(field => {
            let errorMessageWrapper = field.parentElement.querySelector('.field-validation-error');
            let parentElement = field.parentElement;

            const ariaDescribedBy = isEmpty(field.getAttribute('aria-describedby')) ? "" : field.getAttribute('aria-describedby');

            if (!field.validity.valid) {
                if (field.classList.contains('arcgis')) {
                    parentElement = field.nextElementSibling.querySelector('.autocomplete-input')
                }
                if (field.classList.contains('form-dropdown') || field.classList.contains('multi-checkbox')) {
                    const fieldWrapper = field.classList.contains('form-dropdown') ? field.parentElement.querySelector('.btn.dropdown-toggle') : field.parentElement.parentElement;
                    fieldWrapper.setAttribute('aria-describedby', field.id + "-validation" + " " + ariaDescribedBy);
                    fieldWrapper.ariaLive = "assertive";
                    errorMessage = "Oops! Please select one or more options."
                } else {
                    errorMessage = this.fieldErrorMessage;
                    field.setAttribute('aria-describedby', field.id + "-validation" + " " + ariaDescribedBy);
                }

                if (!errorMessageWrapper) {
                    errorMessageWrapper = createErrorMessage(field.id + "-validation")
                    parentElement.appendChild(errorMessageWrapper);
                }
                errorMessageWrapper.textContent = errorMessage;

            } else {
                if (errorMessageWrapper) {
                    errorMessageWrapper.remove();
                    field.removeAttribute('aria-describedby');
                }
            }
        });
        if (allRadioButtonsWrapper && allRadioButtonsWrapper.length > 0) {
            allRadioButtonsWrapper.forEach(wrapper => {
                let errorMessageWrapper = wrapper.parentElement.querySelector('.field-validation-error');
                let parentElement = wrapper.parentElement;
                if (wrapper.hasAttribute('aria-invalid') && wrapper.getAttribute('aria-invalid') == 'true') {
                    errorMessage = "Oops! Please select one or more options."

                    if (!errorMessageWrapper) {
                        errorMessageWrapper = createErrorMessage(wrapper.id + "-validation")
                        parentElement.appendChild(errorMessageWrapper);
                    }
                    errorMessageWrapper.textContent = errorMessage;
                } else {
                    if (errorMessageWrapper) {
                        errorMessageWrapper.remove();
                    }
                }
            })
        }

        if (allMultiCheckboxWrapper && allMultiCheckboxWrapper.length > 0) {
            allMultiCheckboxWrapper.forEach(wrapper => {
                let errorMessageWrapper = wrapper.querySelector('.field-validation-error');
                errorMessage = "Oops! Please select one or more options."
                if (!errorMessageWrapper) {
                    errorMessageWrapper = createErrorMessage(wrapper.id + "-validation");
                    wrapper.appendChild(errorMessageWrapper);
                }
                errorMessageWrapper.textContent = errorMessage;
            });
        }

        function createErrorMessage(id: string) {
            const errorMessageWrapper = document.createElement('div');
            errorMessageWrapper.classList.add('field-validation-error');
            errorMessageWrapper.id = id;
            errorMessageWrapper.role = "alert";
            return errorMessageWrapper;
        }
    }
}

class FormObserver {
    private onlineForm: OnLineForm;
    private observer: MutationObserver | null = null;

    constructor(OnLineForm: OnLineForm) {
        this.onlineForm = OnLineForm;
    }

    // Callback function to execute when mutations are observed
    private callback(mutationsList: MutationRecord[]) {
        // Get the Form
        const formError = this.onlineForm.formContainer.querySelector<HTMLSpanElement>('.formwidget-error');
        const formSuccess = this.onlineForm.formContainer.querySelector<HTMLSpanElement>('.formwidget-submit-text');

        if (!formError && !formSuccess) {
            for (const mutation of mutationsList.filter(mutation => mutation.removedNodes)) {
                const nodes = Array.from(mutation.removedNodes);

                // Check if any of the added nodes contain a form element
                const containsForm = nodes.some((node) => {
                    return node instanceof HTMLElement && node.getElementsByTagName('form') !== null;
                });

                // Form has re-rendered, add re-add the custom submit button and listener
                if (containsForm) {
                    this.onlineForm.reInit();
                    break;
                }
            }
        } else {
            // An error has occured after form submission or the form has been submitted, hide rich text fields
            if (this.onlineForm.beforeText) this.onlineForm.beforeText.style.display = 'none';
            if (this.onlineForm.afterText) this.onlineForm.afterText.style.display = 'none';
            // if success, show success rich text
            if (formSuccess) {
                this.onlineForm.successText.style.display = 'block';
            }
        }
    }

    // Method to start observing mutations
    public startObserving() {
        // Configuration of the observer
        const config: MutationObserverInit = {
            childList: true, // Listen for changes to the children of the target node
            subtree: false // Include all descendant nodes of the target node
        };

        // Create a new observer instance
        this.observer = new MutationObserver((mutationsList) => this.callback(mutationsList));

        // Start observing the target node for configured mutations
        this.observer.observe(this.onlineForm.formContainer, config);
    }
}
